import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const IS_PRODUCTION = import.meta.env.PROD;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: any) => {
    
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response: any) => {
    return response;
  },
  (error: any) => {
    
    // Only clear auth data and redirect for 401 errors on protected routes
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const protectedRoutes = ['/optimize', '/research-landing', '/research-agent-dashboard', '/listing-selection', '/listing-onboarding', '/creative-brief', '/submission-successful', '/copy'];
      
      // Only redirect if we're on a protected route
      if (protectedRoutes.some(route => currentPath.startsWith(route))) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  user_type: 'brands' | 'agencies';
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  last_login: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  user_type: 'brands' | 'agencies';
}

export interface AuthResponse {
  message: string;
  token: string;
  user: User;
}

export interface ApiResponse<T> {
  message?: string;
  error?: string;
  data?: T;
}

class AuthService {
  // Get current user from localStorage
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Get user type from localStorage
  getUserType(): 'brands' | 'agencies' | null {
    const user = this.getCurrentUser();
    return user?.user_type || null;
  }

  // Get auth token from localStorage
  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  // Set auth token in localStorage
  setToken(token: string): void {
    localStorage.setItem('authToken', token);
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  // Check if user is verified
  isVerified(): boolean {
    const user = this.getCurrentUser();
    return user?.is_verified || false;
  }

  // Register new user
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      
      const response = await api.post('/auth/register', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Registration failed');
    }
  }

  // Login user
  async login(data: LoginData): Promise<AuthResponse> {
    try {
      
      const response = await api.post('/auth/login', data);
      
      const { token, user } = response.data;
      
      // Store token and user data
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || 'Login failed';
      
      // If it's an email verification error and we're in production, provide helpful guidance
      if (errorMessage.includes('verify your email') && IS_PRODUCTION) {
        throw new Error('Email verification required. Please check your email for a verification link, or contact support if you need assistance.');
      }
      
      throw new Error(errorMessage);
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  }

  // Verify email
  async verifyEmail(token: string): Promise<ApiResponse<User>> {
    try {
      const response = await api.post('/auth/verify-email', { token });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Email verification failed');
    }
  }

  // Forgot password
  async forgotPassword(email: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Password reset request failed');
    }
  }

  // Reset password
  async resetPassword(token: string, password: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post('/auth/reset-password', { token, password });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Password reset failed');
    }
  }

  // Update user profile
  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    try {
      const response = await api.put('/auth/profile', data);
      const updatedUser = response.data;
      
      // Update stored user data
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Profile update failed');
    }
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Password change failed');
    }
  }

  // Refresh token
  async refreshToken(): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/refresh');
      const { token, user } = response.data;
      
      // Update stored token and user data
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Token refresh failed');
    }
  }
}

const authService = new AuthService();
export default authService; 